/**
 * Mask secret-looking values in arbitrary text before it reaches the model.
 *
 * Two complementary seams use this: real-shell output (`cat .env`, `printenv`) and the
 * `Read` tool (so provider keys stored in `.agent/settings.json` are usable-but-masked).
 * The FS jail hides whole secret FILES by name; this hides secret VALUES wherever they
 * surface in otherwise-legitimate content.
 *
 * Both regexes are linear (no nested quantifiers) — safe against catastrophic backtracking
 * and cheap enough to run on every tool output (see tests/redact.bench).
 */

export const REDACTED = '‹redacted›';

/** Config/control files that may carry provider keys — readers (Read/Grep) mask secret VALUES in
 *  these while keeping the rest readable. (Whole secret FILES like .env are hidden by the FS jail.) */
export const CONFIG_FILE_RE = /(^|\/)\.(agent|claude)\/(settings(\.[\w-]+)?\.json|config\.(json|js|mjs|cjs|ts))$/i;

// (A) `NAME=value` / `"name": "value"` pairs where NAME looks like a secret. Masks the value only,
//     so the agent still sees WHICH key exists (useful config context) without the secret itself.
const SECRET_PAIR =
  /((?:^|[\s,{[])(?:export\s+)?["']?[\w.\-]*(?:KEY|TOKEN|SECRET|PASSWORD|PASSWD|CREDENTIAL|PRIVATE_KEY|ACCESS_?KEY|AUTH(?:_?TOKEN)?|BEARER)[\w.\-]*["']?\s*[:=]\s*)(["']?)([^\s"',{}\]]+)/gi;

// (B) Bare tokens by well-known shape — catches secrets that appear without an obvious key
//     (Authorization headers, URLs, JSON dumps). Conservative prefixes to avoid false positives.
const SECRET_TOKEN =
  /\b(sk-ant-[\w-]{12,}|sk-[A-Za-z0-9]{20,}|ghp_[A-Za-z0-9]{20,}|gho_[A-Za-z0-9]{20,}|github_pat_[\w]{20,}|xox[baprs]-[\w-]{10,}|AKIA[0-9A-Z]{12,}|AIza[\w-]{20,}|eyJ[\w-]{8,}\.[\w-]{8,}\.[\w-]{8,})\b/g;

/** Return `text` with secret values masked. Cheap no-op when nothing matches. */
export function redactSecrets(text: string): string {
  if (!text) return text;
  return text
    .replace(SECRET_PAIR, (_m, head, quote, _val) => `${head}${quote}${REDACTED}`)
    .replace(SECRET_TOKEN, REDACTED);
}
